import React, { useState, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import TimelineIcon from "@mui/icons-material/Timeline";
import CircularProgress from "@mui/material/CircularProgress";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_maintenance_timeline } from "../../../constants";

function MaintenanceHistoryTooltip(props) {
  const { ticketUuid, homeStore, updatedAt } = props;

  const [timeline, setTimeline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  // Invalidate cache when the ticket is updated
  useEffect(() => {
    setTimeline(null);
    setHasEdits(false);
  }, [updatedAt]);

  const fetchTimeline = async () => {
    if (timeline !== null || isLoading) return; // Already loaded or in-flight
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_maintenance_timeline, {
        store_id: homeStore,
        maintenance_request_uuid: ticketUuid,
      });
      const data = res.data?.redux?.payload?.maintenance_timeline || [];
      setTimeline(data);
      setHasEdits(data.length > 1);
    } catch (error) {
      // Leave timeline as null so the next hover retries
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const tooltipContent = (
    <div>
      <h4 className="text-slate-700 text-sm font-bold mb-3">TICKET HISTORY</h4>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
        </div>
      ) : !timeline || timeline.length === 0 ? (
        <p className="text-xs text-slate-500">No history available</p>
      ) : (
        <div className="space-y-4">
          {timeline.map((item, index) => (
            <div key={index} className="relative">
              {/* Timeline connector line */}
              {index !== timeline.length - 1 && (
                <div className="absolute left-1 top-6 bottom-0 w-0.5 bg-slate-200"></div>
              )}

              {/* Timeline entry */}
              <div className="flex items-start gap-3">
                {/* Timeline dot */}
                <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0 bg-violet-700"></div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-900">
                      {item.action}
                    </p>
                    <p className="text-xxs text-slate-500">
                      {formatTimestamp(item.timestamp)}
                    </p>
                  </div>
                  <p className="text-xxs text-slate-600 mb-1">
                    by {item.user}
                  </p>
                  {item.changes && item.changes.length > 0 && (
                    <div className="text-xxs text-slate-500 space-y-0.5">
                      {item.changes.map((change, i) => (
                        <div key={i} className="flex items-start">
                          <span className="mr-1">•</span>
                          <span>
                            {change.old_value
                              ? `${change.field}: ${change.old_value} → ${change.new_value}`
                              : `${change.field}: ${change.new_value}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const iconColor = "text-slate-400";
  const hoverColor = "hover:text-slate-600";

  return (
    <Tooltip
      title={tooltipContent}
      onOpen={fetchTimeline}
      componentsProps={{
        tooltip: {
          sx: {
            maxHeight: "500px",
            overflow: "auto",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow:
              "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
            border: "1px solid #e2e8f0",
            color: "inherit",
            width: "350px",
          },
        },
      }}
    >
      <TimelineIcon
        className={`${iconColor} ${hoverColor} cursor-pointer transition-colors`}
        style={{ fontSize: "16px" }}
      />
    </Tooltip>
  );
}

export default MaintenanceHistoryTooltip;
